import P2PManager from 'p2p-manager-js';
import Peer from './Peer';
import { P2PDefaultSettings, PeerEvent } from '../Utils/Constants';
import Logger from './../Utils/Logger';
/**
 * @class
 * @description P2P manager class, that creates the P2P client and manages the peers and request segments to them.
 * @exports P2PLoader
 */
export default class P2PLoader {
    /**
     * Constructs P2PLoader.
     * @param {Options} options Options object.
     * @param {SegmentStorage} storage SegmentStore object.
     */
    constructor(options, storage) {
        this.isEnabled = false;
        this.upload = P2PDefaultSettings.upload;
        this._maxConcurrent = P2PDefaultSettings.maxConcurrency;
        this._peers = new Map();
        this._candidates = new Map();
        this._activeDownloads = 0;
        this._segmentsRequested = new Map();
        this._activeDownloadIds = [];
        /** Statistic params */
        this._connectedPeers = 0;
        // Download timeout bytes
        this._timeoutDiscardedBytes = 0;
        this._failedRequests = 0;
        this._timeoutRequests = 0;
        this._segmentAbsentRequests = 0;
        this._errorRequests = 0;
        // Acum download time, bytes and chunks
        this._downloadMillis = 0;
        this._byteDownloadCount = 0;
        this._chunkDownloadCount = 0;
        // Upload timeout bytes and chunks
        this._byteUploadDiscardedCount = 0;
        this._chunkUploadDiscardedCount = 0;
        // Upload successful bytes and chunks
        this._chunkUploadCount = 0;
        this._byteUploadCount = 0;
        this._options = options;
        this._storage = storage;
    }
    /**
     * Sets P2P settings from API response.
     * @param {balancerResponse} e API response.
     * @public
     */
    setSettings(e) {
        if (!this.isEnabled) {
            if (e.p2p && e.p2p.enabled) {
                Logger.log("P2P support enabled.");
                this.isEnabled = true;
                this.upload = !e.p2p.consumeOnly || P2PDefaultSettings.upload;
                this._maxConcurrent = e.p2p.maxParallelP2PDownloads || P2PDefaultSettings.maxConcurrency;
                const url = new URL(this._options.resource);
                const contentHash = this._options.accountCode + (this._options.videoId || url.pathname);
                this.destroy();
                this._client = new P2PManager(contentHash, {
                    tracker: e.p2p.trackerAnnounce || P2PDefaultSettings.tracker,
                    accountCode: this._options.accountCode,
                    //iceServers
                    announcePeers: e.p2p.peerRequestsPerAnnounce
                    //maxAnnouncePeers
                    //token
                });
                this._client
                    .on(P2PManager.Constants.WebRtcEvents.Error, P2PLoader._errorListener.bind(this))
                    .on(P2PManager.Constants.WebRtcEvents.Warning, P2PLoader._warningListener.bind(this))
                    .on(P2PManager.Constants.WebRtcEvents.Update, this._sendMapToAllPeers.bind(this))
                    .on(P2PManager.Constants.WebRtcEvents.Peer, this._peerListener.bind(this))
                    .on(P2PManager.Constants.WebRtcEvents.PeerUpdate, this._sendMapToAllPeers.bind(this))
                    .start();
            }
            else {
                Logger.log("P2P support disabled.");
                this.isEnabled = false;
            }
        }
    }
    /**
     * @param {string} id Id of the segment.
     * @returns {VideoSegment|undefined} Segment object corresponding to the id, or undefined.
     * @public
     */
    getSegment(segment) {
        //  let segment = this._segmentsRequested.get(id);
        //   if( segment )
        //     return segment;
        return this._storage.getSegment(segment.p2pId);
    }
    /**
    * @param {string} id Id of the segment.
    * @returns {VideoSegment|undefined} Segment object corresponding to the id, or undefined.
    * @public
    */
    /*  public getRequested( id: string ): VideoSegment | undefined {
            return this._storage.getSegment(id);
      }*/
    /**
     * Tries to check if the segment is available from any peer, and returns if it is or not.
     * @param {VideoSegment} seg Video segment object to be requested.
     * @returns {boolean} If the segment can be downloaded using P2P or not.
     * @public
     */
    request(segment) {
        // return false;
        var _a, _b, _c, _d;
        Logger.log("P2P Requesting new segment " + segment.p2pId + " | " + ((_a = segment.rendition) === null || _a === void 0 ? void 0 : _a.isAudio) + " peers: " + this._peers.size);
        let canBeDownloaded = false;
        Logger.log("Active downloads vs max concurrencui P2PMO -> " + this._activeDownloads + " " + this._maxConcurrent);
        if (this._activeDownloads < this._maxConcurrent) {
            this._segmentsRequested.set(segment.p2pId, segment);
            for (const [, peer] of this._peers) {
                Logger.log("P2P Requesting segment " + segment.p2pId + "  | " + ((_b = segment.rendition) === null || _b === void 0 ? void 0 : _b.isAudio) + " to peer " + peer.id + " available: " + canBeDownloaded + ", peer map size: " + peer.getSegmentMap().length + " " + segment.url);
                if (!peer.isDownloading() && peer.getSegmentMap().indexOf(segment.p2pId) >= 0) {
                    this._activeDownloads++;
                    segment.triedP2P = true;
                    peer.request(segment.p2pId);
                    Logger.log("P2P Requesting segment " + segment.p2pId + "  audio -> " + (((_c = segment.rendition) === null || _c === void 0 ? void 0 : _c.isAudio) + "no", "yes") + " available from peer " + peer.id + " size -> " + peer.getSegmentMap().length + " pos: " + peer.getSegmentMap().indexOf(segment.p2pId));
                    return true;
                }
            }
        }
        else {
            Logger.log("P2P Unable to request to more peers max concurrency (" + this._maxConcurrent + ") already reached");
        }
        Logger.log("P2P Requesting segment " + segment.p2pId + "  audio -> " + ((_d = segment.rendition) === null || _d === void 0 ? void 0 : _d.isAudio) + " not available ");
        return false;
    }
    /**
     * Callback for error event from P2P Client.
     * @param {Object} e Error event data.
     * @private
     * @static
     */
    static _errorListener(e) {
        console.error('P2P Error: ', e);
    }
    /**
     * Callback for warning event from P2P Client.
     * @param {Object} e Warning event data.
     * @private
     * @static
     */
    static _warningListener(e) {
        console.warn('P2P Warning ', e);
    }
    /**
     * If p2p upload enabled, it sets the partially downloaded segment to share it with peers.
     * @param _newSegment Segment to store.
     * @public
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newSegmentInProgress(newSegment) {
        /*if (this.upload) {
          if (this._activeDownloadIds.indexOf(newSegment.id) === -1) {
            // If its first progress, report it
            this._sendMapToAllPeers();
          }
          this._activeDownloadIds.push(newSegment.id);
        }*/
    }
    /**
     * If p2p upload enabled, it stores the downloaded segment to share it with peers.
     * @param segment Segment to store.
     * @param data Data to store in the segment in memory.
     * @public
     */
    storeSegment(segment) {
        //Logger.log("P2P Storing segment -> "+newSegment.url);
        if (this.upload) {
            this._storage.storeSegment(segment);
            this._sendMapToAllPeers();
            const index = this._activeDownloadIds.indexOf(segment.p2pId);
            if (index >= 0) {
                this._activeDownloadIds.splice(index);
            }
        }
    }
    /**
     * Method to be called when we want to notify the peers that we have an updated map (list of video segments).
     * @private
     */
    _sendMapToAllPeers() {
        const map = this._storage.createMap();
        this._peers.forEach((peer) => {
            Logger.log("P2P Send map to " + peer.id + " map size -> " + map.length);
            peer.sendMap(map);
        });
    }
    /**
     * Callback for new peer candidate event from P2P Client.
     * @param {callbackData} e Peer information to listen.
     * @private
     */
    _peerListener(e) {
        var _a;
        Logger.log("P2P received message from " + e.data.id);
        if (!this._peers.has(e.data.id)) {
            if (!this._candidates.has(e.data.id)) {
                this._candidates.set(e.data.id, []);
            }
            const newPeer = new Peer(e.data);
            (_a = this._candidates.get(e.data.id)) === null || _a === void 0 ? void 0 : _a.push(newPeer);
            this._listenCandidate(newPeer);
            Logger.log("P2P New peer candidate " + newPeer.id);
        }
        else {
            // We don't have to destroy the peer here
            // e.data.destroy();
        }
        this._sendMapToAllPeers();
    }
    /**
     * Listens the peer events to add it to actives or remove them.
     * @param {peer} peer Peer instance added to candidate list.
     * @private
     */
    _listenCandidate(peer) {
        peer.on(PeerEvent.connect, this._peerConnectListener.bind(this));
        peer.on(PeerEvent.close, this._peerCloseListener.bind(this));
        peer.on(PeerEvent.segmentRequest, this._peerSegmentRequest.bind(this));
        peer.on(PeerEvent.segmentLoaded, this._peerSegmentLoaded.bind(this));
        peer.on(PeerEvent.segmentProgress, this._peerSegmentProgress.bind(this));
        peer.on(PeerEvent.segmentAbsent, this._peerFailedSegment.bind(this));
        peer.on(PeerEvent.segmentError, this._peerFailedSegment.bind(this));
        peer.on(PeerEvent.segmentTimeout, this._peerFailedSegment.bind(this));
        peer.on(PeerEvent.bytesUploaded, this._uploadedSegment.bind(this));
        peer.on(PeerEvent.bytesUploadedDiscarded, this._discaredSegment.bind(this));
        peer.on(PeerEvent.ping, this._discaredSegment.bind(this));
    }
    /**
     * If the peer is connected and didnt exist adds it to peers list and removes the candidates.
     * If it existed before and was connected, it deletes it.
     * @param {Object} e Event object with event name and the new peer.
     * @private
     */
    _peerConnectListener(e) {
        var _a;
        const peer = e.data;
        this._connectedPeers++;
        if (this._peers.has(peer.id)) {
            Logger.log("P2P peer already exist. " + peer.id);
            peer.destroy();
        }
        else {
            Logger.log("P2P adding the candidate. " + peer.id);
            this._peers.set(peer.id, peer);
            (_a = this._candidates.get(peer.id)) === null || _a === void 0 ? void 0 : _a.forEach((candidate) => {
                if (peer != candidate)
                    candidate.destroy();
            });
            this._candidates.delete(peer.id);
        }
        this._sendMapToAllPeers();
    }
    /**
     * Callback of close peer event. Will remove it from candidates and peers.
     * @param {Object} e Event object with event name and the peer to remove.
     * @private
     */
    _peerCloseListener(e) {
        const peer = e.data;
        this._peers.delete(peer.id);
        const candidateList = this._candidates.get(peer.id);
        if (candidateList) {
            const ind = candidateList.indexOf(peer);
            if (ind > -1) {
                candidateList.splice(ind, 1);
                if (!candidateList.length) {
                    this._candidates.delete(peer.id);
                }
            }
        }
    }
    /**
     * Callback of peer segment request event. Given a request if available it returns the chosen segment.
     * @param {Object} e Callback data from peer segment request event.
     * @private
     */
    _peerSegmentRequest(e) {
        const peer = e.data.peer;
        const segment = this._storage.getSegment(e.data.segment);
        segment ? peer.sendSegment(e.data.segment, segment, e.data.identifier) : peer.sendNoSegment(e.data.segment);
        Logger.log("P2P Peer requested segment -> " + e.data.segment + " " + (segment === null || segment === void 0 ? void 0 : segment.size) + " " + (segment === null || segment === void 0 ? void 0 : segment.data.byteLength) + " | " + peer.id + " " + e.data.identifier);
    }
    /**
     * Callback from peer segment loaded. It gets the data and adds it to the segment object,
     * then triggers the success event of it.
     * @param {Object} e Callback data from peer segment loaded event.
     * @private
     */
    _peerSegmentLoaded(e) {
        var _a;
        const id = e.data.id;
        const data = e.data.data;
        this._activeDownloads--;
        this._chunkDownloadCount++;
        this._downloadMillis += e.data.time;
        this._byteDownloadCount += data.byteLength;
        (_a = this._segmentsRequested.get(id)) === null || _a === void 0 ? void 0 : _a.setContent(data);
        // store de downlaoded segments to the local storage
        let segment = this._segmentsRequested.get(id);
        if (segment)
            this.storeSegment(segment);
    }
    /**
   * Callback from peer segment loading progress. It gets the data and adds it to the segment object,
   * then triggers the success event of it.
   * @param {Object} e Callback data from peer segment loaded event.
   * @private
   */
    _peerSegmentProgress(e) {
        const id = e.data.id;
        const data = e.data.data;
        const size = e.data.size;
        let segment = this._segmentsRequested.get(id);
        if (segment && !this._storage.getSegment(segment.id)) {
            segment.realBodySize = e.data.size;
            this.storeSegment(segment);
        }
        if (segment) {
            segment.data = e.data.data;
        }
    }
    /**
     * Callback from peer segment abort, error and timeout. It rejects the request and triggers failed segment event.
     * @param {Object} e Callback data from peer segment events.
     * @private
     */
    _peerFailedSegment(e) {
        var _a;
        switch (e.type) {
            case PeerEvent.segmentAbsent:
                this._segmentAbsentRequests++;
                break;
            case PeerEvent.segmentTimeout:
                this._timeoutDiscardedBytes += e.data.size || 0;
                this._timeoutRequests++;
                break;
            case PeerEvent.segmentError:
                this._errorRequests++;
                break;
        }
        const id = e.data.id;
        this._activeDownloads--;
        this._failedRequests++;
        (_a = this._segmentsRequested.get(id)) === null || _a === void 0 ? void 0 : _a.noContentObtained();
    }
    /**
     * Callback from peer segment uploaded. Counts the downloaded bytes and segments.
     * @param {Object} e callback data from peer segment uploaded event.
     * @private
     */
    _uploadedSegment(e) {
        this._byteUploadCount += e.data;
        this._chunkUploadCount++;
    }
    /**
     * Callback from peer segment request cancelled. Counts the uploaded bytes that wont be used by the peer.
     * @param {Object} e callback data from peer segment upload failed event.
     * @private
     */
    _discaredSegment(e) {
        this._byteUploadDiscardedCount += e.data;
        this._chunkUploadDiscardedCount++;
    }
    /**
     * Destroys the client and the peers/candidates.
     * To be called when view is over and content switched.
     * @public
     */
    destroy() {
        if (this._client) {
            /*[ClientEvent.error, ClientEvent.peer, ClientEvent.update, ClientEvent.warning].forEach((event) => {
              this._client?.removeAllListeners(event);
            });*/
            this._client.destroy();
            delete this._client;
        }
        for (const [, peerList] of this._candidates) {
            peerList.forEach((peer) => {
                peer.destroy();
            });
        }
        for (const [, peer] of this._peers) {
            peer.destroy();
        }
    }
    /**
     * Returns the timestamp of the oldest request stored from all the peers.
     * @returns {number} Timestamp of the oldest request stored.
     * @public
     */
    getOldestRequestTS() {
        let oldest = Date.now();
        this._peers.forEach((peer) => {
            const resp = peer.getOldestRequestTS();
            if (resp) {
                oldest = Math.min(oldest, resp);
            }
        });
        return oldest;
    }
    getPeers() {
        return this._peers;
    }
    /**
     * Returns an object with the P2P data stats of the current content/view.
     * @returns {P2PLoaderStats} P2P info object.
     * @public
     */
    getStats() {
        let downloadAvgBw = 0;
        let downloadPeers = 0;
        let lastSecondsDownloadTraffic = 0;
        let lastSecondsUploadTraffic = 0;
        this._peers.forEach((peer) => {
            if (peer.downloadBw > 0) {
                downloadAvgBw += peer.downloadBw;
                downloadPeers++;
            }
            lastSecondsDownloadTraffic += peer.getLastDownloadsTraffic();
            lastSecondsUploadTraffic += peer.getLastUploadsTraffic();
        });
        downloadAvgBw /= downloadPeers || 1;
        downloadAvgBw = Math.trunc(downloadAvgBw);
        return {
            //Settings
            downloadEnabled: true,
            uploadEnabled: this.upload,
            //Upload
            uploadedBytes: this._byteUploadCount,
            uploadedSegments: this._chunkUploadCount,
            //Upload failures
            discardedUploadedChunks: this._chunkUploadDiscardedCount,
            discardedUploadedBytes: this._byteUploadDiscardedCount,
            //Download
            downloadedSegments: this._chunkDownloadCount,
            downloadedBytes: this._byteDownloadCount,
            //Download failures
            discardedDownloadedBytes: this._timeoutDiscardedBytes,
            failedRequests: {
                total: this._failedRequests,
                absent: this._segmentAbsentRequests,
                timeout: this._timeoutRequests,
                error: this._errorRequests
            },
            // Peer stats
            activePeers: this._peers.size,
            totalPeers: this._connectedPeers,
            downloadMillis: this._downloadMillis,
            downloadAvgBw,
            lastSecondsDownloadTraffic,
            lastSecondsUploadTraffic
        };
    }
}
